// Come back to the same place after a browser refresh (owner, 2026-10-17): the tab,
// sub-tab, open document, chosen client … a screen was showing before F5 is shown again.
//
// The pillar pages already keep their sub-page in the URL hash; this covers the state
// that lives inside a screen. It is kept in sessionStorage (this browser tab only, gone
// when the tab closes) under the page path + hash, so DMS and Your Accounts each keep their own.
//
// Only a REFRESH restores it. Arriving at a screen any other way — the sidebar, a menu,
// re-clicking a tab, a link from another screen — still lands on the screen's landing
// state (the guard-then-land rule in CLAUDE.md). So each saved value is handed back at
// most once per page load: to the first screen that asks for it after the reload.
import { useEffect, useRef, useState } from 'react';

const PREFIX = 'akt_refresh:';

const IS_RELOAD = (() => {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'reload';
  } catch { return false; }
})();

// Keys already handed back since this page load (a later remount lands as usual).
const consumed = new Set<string>();

// Path + hash: the hash names the pillar's sub-page, and one component can sit on two of
// them (Process Docs and DMS → Folder List are both ProcessReceiptsTab). A key starting
// with "page:" is for the whole page instead (e.g. the client chosen in Client's Accounts,
// shared by all its sub-pages).
const fullKey = (key: string) => key.startsWith('page:')
  ? `${PREFIX}${window.location.pathname}:${key}`
  : `${PREFIX}${window.location.pathname}${window.location.hash}:${key}`;

/** The value saved under `key` before this refresh, or undefined (not a refresh / already used / none). */
export function readRefreshValue<T>(key: string): T | undefined {
  const k = fullKey(key);
  if (!IS_RELOAD || consumed.has(k)) return undefined;
  try {
    const raw = sessionStorage.getItem(k);
    return raw == null ? undefined : (JSON.parse(raw) as T);
  } catch { return undefined; }
}

/** Mark `key` as used for this page load — later mounts start at their landing state. */
export function consumeRefreshValue(key: string): void {
  consumed.add(fullKey(key));
}

/** Remember `value` under `key` so a refresh can bring it back. null/undefined forgets it. */
export function writeRefreshValue(key: string, value: unknown): void {
  try {
    const k = fullKey(key);
    if (value === undefined || value === null) sessionStorage.removeItem(k);
    else sessionStorage.setItem(k, JSON.stringify(value));
  } catch { /* storage unavailable — a refresh just lands on the default */ }
}

/**
 * useState that survives a browser refresh. `isValid` rejects a saved value that no
 * longer fits (a tab that was renamed or is not offered here).
 * Marked used in an effect, not the initializer: StrictMode runs initializers twice.
 */
export function useRefreshState<T>(
  key: string,
  initial: T | (() => T),
  isValid?: (v: unknown) => boolean,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const saved = readRefreshValue<T>(key);
    if (saved !== undefined && (!isValid || isValid(saved))) return saved;
    return typeof initial === 'function' ? (initial as () => T)() : initial;
  });
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => { consumeRefreshValue(key); }, [key]);
  useEffect(() => { writeRefreshValue(keyRef.current, value); }, [value]);
  return [value, setValue];
}
